






















import { Component, Prop, Watch, Emit, Mixins } from "vue-property-decorator";
import { Rectangle, Point, Vector } from "../../../domain/drawing";
import { IDrawableItem, AbstractVectorItem, AbstractPointsItem } from "../../../domain/document/items";
import { AsyncRequestPool } from "../../../utilities";
import { LocatableMixin, RectangleLayoutableMixin, LinearLayoutableMixin, PointsLayoutableMixin } from "../../system/mixins";
import DrawableObject from "./DrawableObject.vue";
import { IObjectLocatable } from "./IObjectLocatable";

@Component({
  components: {
    drawableObject: DrawableObject,
  },
})
export default class ObjectLocator extends Mixins(LocatableMixin, RectangleLayoutableMixin, LinearLayoutableMixin, PointsLayoutableMixin) implements IObjectLocatable {
  @Prop()
  zIndex: number;

  data(): any {
    return {
      requestPool: new AsyncRequestPool<any, IDrawableItem>(r => this.located(r.source)),
      source: null,
    };
  }
  requestPool: AsyncRequestPool<any, IDrawableItem>;
  source: IDrawableItem;

  get rootStyle(): any {
    return {
      zIndex: this.zIndex,
    };
  }

  get message(): string {
    if (this.isLocating) {
      return "オブジェクトを追加する位置でクリックしてください。";
    } else if (this.isRectangleLayouting) {
      return "オブジェクトを配置する位置(左上)から、オブジェクトのサイズ分ドラッグしてください。";
    } else if (this.isLinearLayouting) {
      return "直線の始点から終点までドラッグしてください。";
    } else if (this.isPointsLayouting) {
      return "自由曲線の始点から終点までドラッグしてください。";
    } else {
      return "";
    }
  }

  get isVisible(): boolean {
    return this.isLocating
      || this.isRectangleLayouting
      || this.isLinearLayouting
      || this.isPointsLayouting;
  }

  get isObjectVisible(): boolean {
    return this.requestPool.isProcessing
      && (this.isLocatorVisible || this.isRectangleLayouterVisible || this.isLinearLayouterVisible || this.isPointsLayouterVisible);
  }

  @Watch("locatorRectangle")
  onLocatorRectangleUpdated(newValue: Rectangle, oldValue: Rectangle): void {
    this.onObjectRectangleUpdated(newValue, oldValue);
  }

  @Watch("layouterRectangle")
  onLayouterRectangleUpdated(newValue: Rectangle, oldValue: Rectangle): void {
    this.onObjectRectangleUpdated(newValue, oldValue);
  }

  private onObjectRectangleUpdated(newValue: Rectangle, oldValue: Rectangle): void {
    if (!this.isObjectVisible) {
      return;
    }
    this.updateSource<IDrawableItem>(s => {
      if (s instanceof AbstractVectorItem) {
        const difference = newValue.topLeft.subtract(oldValue.topLeft);
        s.vector = s.vector.move(difference);
      } else {
        s.geometry.rectangle = newValue;
      }
    });
  }

  @Watch("layouterLinear")
  onLayouterLinearUpdated(newValue: Vector, oldValue: Vector): void {
    if (!this.isObjectVisible) {
      return;
    }
    this.updateSource<AbstractVectorItem>(s => {
      s.vector = newValue;
    });
  }

  @Watch("layouterPoints")
  onlayouterPointsUpdated(newValue: Point[], oldValue: Point[]): void {
    if (!this.isObjectVisible) {
      return;
    }
    this.updateSource<AbstractPointsItem>(s => {
      s.points = newValue;
    });
  }

  updateSource<T extends IDrawableItem>(update: (source: T) => void): void {
    if (this.requestPool.isProcessing && this.requestPool.processingRequest.source) {
      const clone = this.requestPool.processingRequest.source.clone() as T;
      update(clone);
      this.requestPool.processingRequest.source = clone;
      this.source = clone;
    }
  }

  @Emit()
  located(located: IDrawableItem): void {}

  async locateItem(requester: any, source: IDrawableItem): Promise<Point> {
    return this.request<Point>(
      source,
      requester,
      () => this.locate(source.geometry.rectangle.size),
    );
  }

  async locateItemVertically(requester: any, source: IDrawableItem): Promise<Point> {
    return this.request<Point>(
      source,
      requester,
      () => this._locateVertically(source.geometry.rectangle),
    );
  }

  async selectRectangle(requester: any): Promise<Rectangle> {
    return this.request<Rectangle>(
      null,
      requester,
      () => this.layoutRectangle(0),
    );
  }

  async layoutRectangleItem(requester: any, source: IDrawableItem, fixedAspectRatio: number = 0): Promise<Rectangle> {
    return this.request<Rectangle>(
      source,
      requester,
      () => this.layoutRectangle(fixedAspectRatio),
    );
  }

  async layoutLinearItem(requester: any, source: IDrawableItem): Promise<Vector> {
    return this.request<Vector>(
      source,
      requester,
      () => this.layoutLinear(),
    );
  }

  async layoutPointsItem(requester: any, source: IDrawableItem): Promise<Point[]> {
    return this.request<Point[]>(
      source,
      requester,
      () => this.layoutPoints(),
    );
  }

  private async request<T>(source: IDrawableItem, requester: any, request: () => Promise<any>): Promise<T> {
    this.source = source;
    const result = await this.requestPool.request(source, requester, request) as T;
    this.source = null;
    return result;
  }

  cancel(requester: any): void {
    if (this.requestPool.cancel(requester)) {
      // 実行中リクエストのキャンセル.
      this.cancelLocating();
      this.cancelRectangleLayouting();
      this.cancelLinearLayouting();
      this.cancelPointsLayouting();
    }
  }
}
